<script lang="ts" setup>
import { AanvragenDomainEntitiesMutatiesoort as Mutatiesoort } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesMutatiesoort';
import { type AanvragenMuterenRoyementDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenRoyementDto';
import { type SUREbusinessCommonDomainSoortVerzekeringEnum } from '@services/aanvragen-public/orval-api/model/sUREbusinessCommonDomainSoortVerzekeringEnum';
import { useMutatieRedenenGet } from '@services/aanvragen-public/orval-api/mutatie-redenen-api/mutatie-redenen-api';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import { parseDateOnlyAsLocalTime } from '@tools/dates';
import { logger } from '@logging/logging';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useId } from '@tools/composables/id';
import { z } from 'zod';
import { usePakketpolisStore } from '../pakketpolisStore';

const props = defineProps<{
  polisnummer: string;
  soortVerzekeringen: SUREbusinessCommonDomainSoortVerzekeringEnum[] | undefined;
  royeerInformatie: AanvragenMuterenRoyementDto;
}>();

const { polisnummer } = toRefs(props);

const pakketpolisStore = usePakketpolisStore();

const extraToelichtingNodigBijRoyementVoorDatum = computed(() =>
  parseDateOnlyAsLocalTime(props.royeerInformatie.extraToelichtingNodigBijRoyementVoorDatum),
);
const royementAlleenMogelijkVanafDatum = computed(() =>
  parseDateOnlyAsLocalTime(props.royeerInformatie.alleenMogelijkVanafDatum),
);

// TODO zodra soortverzekering PP in beheerbox is ingeregeld hier integreren
const { data, isPending } = useMutatieRedenenGet(
  computed(() => ({
    mutatiesoort: Mutatiesoort.Royement,
    soortVerzekering: undefined,
  })),
  {
    query: {
      select: (res) => res.result,
    },
  },
);
const validationSchema = toTypedSchema(
  z
    .object({
      gekozenReden: z.string().min(1),
      redenAnders: z
        .string()
        .min(1, 'De royementsreden is verplicht.')
        .max(40, 'De royementsreden mag maximaal 40 karakters bevatten')
        .optional(),
      redenRoyeerdatum: z
        .string()
        .min(10, 'De toelichting moet tenminste 10 tekens bevatten.')
        .optional(),
      gekozenDatum: z.date(),
    })
    .refine((input) => {
      if (
        input.gekozenReden === 'anders' &&
        (!input.redenAnders || !input.redenAnders.trim().length)
      )
        return false;
      if (
        extraToelichtingNodigBijRoyementVoorDatum.value !== undefined &&
        input.gekozenDatum < extraToelichtingNodigBijRoyementVoorDatum.value &&
        (!input.redenRoyeerdatum || input.redenRoyeerdatum.trim().length < 10)
      )
        return false;

      return true;
    }),
);

const { errors, useFieldModel, validate } = useForm({
  validationSchema,
  initialValues: {
    gekozenDatum: new Date(),
  },
});

const gekozenReden = useFieldModel('gekozenReden');
const gekozenDatum = useFieldModel('gekozenDatum');
const redenAnders = useFieldModel('redenAnders');
const redenRoyeerdatum = useFieldModel('redenRoyeerdatum');

const andereRedenRef = ref();
const redenRoyeerdatumRef = ref();

const extraToelichtingNodig = computed(
  () =>
    !!extraToelichtingNodigBijRoyementVoorDatum.value &&
    !!gekozenDatum.value &&
    gekozenDatum.value < extraToelichtingNodigBijRoyementVoorDatum.value,
);

watch(gekozenReden, (e) =>
  nextTick(() => {
    if (e === 'anders') andereRedenRef?.value.focus();
  }),
);

watch(extraToelichtingNodig, (newValue) => {
  if (newValue) {
    nextTick(() => redenRoyeerdatumRef.value?.focus());
  }
});

watch(
  royementAlleenMogelijkVanafDatum,
  (newValue) => {
    gekozenDatum.value = newValue ?? new Date();
  },
  { immediate: true },
);

const options = computed(
  () =>
    data.value?.map((x) => ({
      label: x.omschrijving + (x.interneOptie ? ' (Interne optie)' : ''),
      value: x.omschrijving,
    })) ?? [],
);

const isOpen = defineModel<boolean>('isOpen');
const royementdatumId = useId('royementdatum');

async function opslaan() {
  const validationResult = await validate();
  if (!validationResult.valid) {
    logger.debug('validationResult', validationResult.results);
    return;
  }

  pakketpolisStore.setPakketpolisGeroyeerd(
    polisnummer.value,
    gekozenDatum.value!,
    gekozenReden.value === 'anders' ? redenAnders.value! : gekozenReden.value!,
    extraToelichtingNodig.value ? redenRoyeerdatum.value! : undefined,
  );

  isOpen.value = false;
}
</script>
<template>
  <VModal
    overflow
    confirm-button-text="Royement opslaan"
    @close-modal="isOpen = false"
    @confirm-action="opslaan()"
    invert-header
    title="Royement pakket"
    :is-active="isOpen"
    data-testid="pakket-polis-royeer-modal"
  >
    <FieldRowGroup :gap="2">
      <FieldRowV2 label-on-own-row label="Reden royement" :errors="[errors.gekozenReden]">
        <VSkeleton height="lg" v-if="isPending" />
        <SelectField
          v-model:value="gekozenReden"
          v-else
          :options="[...options, { label: 'Andere reden...', value: 'anders' }]"
        />
      </FieldRowV2>
      <FieldRowV2 :errors="gekozenReden === 'anders' ? [errors.redenAnders] : []">
        <ExpandTransition>
          <TextField
            v-if="gekozenReden === 'anders'"
            :rows="2"
            :max-length="40"
            v-model:value="redenAnders"
            ref="andereRedenRef"
            placeholder="Geef een royementsreden op"
          />
        </ExpandTransition>
      </FieldRowV2>
      <div class="flex flex-col">
        <VInfoBox
          type="info"
          badge-text="belangrijk"
          v-if="royementAlleenMogelijkVanafDatum !== undefined"
          class="mt-2"
          data-testid="toekomstpolis-melding"
        >
          Het pakket is nog niet ingegaan. Royementen kunnen alleen op of na de ingangsdatum
          plaatsvinden.
        </VInfoBox>
        <FieldRowV2
          class="mt-2"
          label-on-own-row
          label="Datum royement"
          :label-for="royementdatumId"
          :errors="[errors.gekozenDatum]"
        >
          <VDatePicker
            :id="royementdatumId"
            v-model="gekozenDatum"
            :invalid="!!errors?.gekozenDatum"
            :clearable="false"
            :min-date="royementAlleenMogelijkVanafDatum"
            data-testid="royementsdatum"
          />
        </FieldRowV2>
      </div>
      <ExpandTransition>
        <div v-if="extraToelichtingNodig" class="flex flex-col gap-4 !duration-400">
          <VInfoBox type="warning" badge-text="Extra toelichting nodig">
            We nemen alleen royementen in behandeling tot
            <span class="font-bold">14 dagen</span> in het verleden. Wil je hieronder zo uitgebreid
            mogelijk aangeven waarom dit royement verder in het verleden dient te worden uitgevoerd?
          </VInfoBox>
          <FieldRowV2 :errors="[errors.redenRoyeerdatum]">
            <TextField
              :rows="6"
              v-model:value="redenRoyeerdatum"
              ref="redenRoyeerdatumRef"
              placeholder="Toelichting bij royement."
            />
          </FieldRowV2>
        </div>
      </ExpandTransition>
    </FieldRowGroup>
  </VModal>
</template>
